import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ImagePlus, Trash2 } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";
import { shrinkImage } from "@/lib/images";
import { PAGES_QUERY } from "@/hooks/usePages";

const MAX = 40;
const iconBtn = "grid place-items-center h-8 w-8 rounded-full bg-ink/70 text-cream hover:bg-cream hover:text-ink transition-colors disabled:opacity-30";

function Thumb({ g, i, count, onMove, onRemove, onCaption }) {
  const [caption, setCaption] = useState(g.caption || "");
  return (
    <li className="rounded-xl overflow-hidden border border-cream/10 bg-cream/5" data-testid={`gallery-thumb-${i}`}>
      <div className="relative aspect-[4/3]">
        <img src={assetUrl(g.src)} alt={g.caption || `Photo ${i + 1}`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute top-2 inset-x-2 flex justify-between">
          <div className="flex gap-1">
            <button onClick={() => onMove(i, -1)} disabled={i === 0} className={iconBtn} aria-label="Move left" data-testid={`gallery-thumb-left-${i}`}><ArrowLeft className="h-4 w-4" /></button>
            <button onClick={() => onMove(i, 1)} disabled={i === count - 1} className={iconBtn} aria-label="Move right" data-testid={`gallery-thumb-right-${i}`}><ArrowRight className="h-4 w-4" /></button>
          </div>
          <button onClick={() => onRemove(i)} className={`${iconBtn} hover:!bg-red-500 hover:!text-white`} aria-label="Remove photo" data-testid={`gallery-thumb-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <input value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== (g.caption || "") && onCaption(i, caption.trim())}
        maxLength={80} placeholder="Caption (optional)" className="w-full bg-transparent px-3 py-2 text-sm text-cream outline-none placeholder:text-cream/40"
        data-testid={`gallery-thumb-caption-${i}`} />
    </li>
  );
}

export function GalleryEditor({ page }) {
  const qc = useQueryClient();
  const input = useRef(null);
  const [busy, setBusy] = useState("");
  const items = Array.isArray(page.gallery) ? page.gallery : [];

  const save = async (gallery, ok) => {
    try {
      await api.put(`/admin/pages/${page.id}`, { gallery });
      await qc.invalidateQueries({ queryKey: PAGES_QUERY });
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const add = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX - items.length);
    e.target.value = "";
    if (!files.length) return;
    const added = [];
    for (const [n, f] of files.entries()) {
      setBusy(`Uploading ${n + 1} of ${files.length}…`);
      try {
        const fd = new FormData();
        fd.append("file", await shrinkImage(f));
        added.push({ src: (await api.post("/admin/uploads", fd)).data.url, caption: "" });
      } catch (err) {
        toast.error(`${f.name}: ${errMsg(err)}`);
      }
    }
    setBusy("");
    if (added.length) await save([...items, ...added], `${added.length} photo${added.length > 1 ? "s" : ""} added`);
  };

  const move = (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    save(next);
  };
  const remove = (i) => window.confirm("Remove this photo from the gallery?") && save(items.filter((_, j) => j !== i), "Photo removed");
  const caption = (i, c) => save(items.map((g, j) => (j === i ? { ...g, caption: c } : g)), "Caption saved");

  return (
    <div className="rounded-xl border border-cream/10 bg-ink/40 p-4 lg:col-span-2" data-testid={`gallery-editor-${page.slug}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-cream/75">Photo gallery ({items.length}/{MAX}). Visitors can swipe through these photos on the page and tap one to enlarge it.</p>
        <button onClick={() => input.current?.click()} disabled={!!busy || items.length >= MAX}
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-terra px-4 py-2 text-sm font-semibold text-paper hover:bg-terra-soft disabled:opacity-60" data-testid={`gallery-add-btn-${page.slug}`}>
          <ImagePlus className="h-4 w-4" /> {busy || "Add photos"}
        </button>
        <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={add} data-testid={`gallery-add-input-${page.slug}`} />
      </div>
      {items.length === 0
        ? <p className="text-sm text-cream/60">No photos yet. You can pick several at once.</p>
        : <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {items.map((g, i) => <Thumb key={`${g.src}-${i}-${g.caption}`} g={g} i={i} count={items.length} onMove={move} onRemove={remove} onCaption={caption} />)}
          </ul>}
    </div>
  );
}
